"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Truck, PackageCheck, Warehouse, ArrowRight, ShieldAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function DeliveryPlanning() {
  const shouldReduceMotion = useReducedMotion();
  const logisticsPillars = [{
    icon: Truck,
    label: "FREIGHT TRANSIT",
    detail: "Dedicated LTL carrier dispatch"
  }, {
    icon: Warehouse,
    label: "FACILITY CLEARANCE",
    detail: "Dock or liftgate staging required"
  }, {
    icon: PackageCheck,
    label: "MULTI-PIECE CONSIGNMENT",
    detail: "Modular palletized components"
  }];
  return <section id="deliveryplanningcallout" data-nav="light" className="relative bg-background py-16 md:py-24 border-y border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative bg-card text-card-foreground border border-border p-6 sm:p-10 md:p-12 shadow-sm">
          {/* Industrial Corner Ticks */}
          <div aria-hidden="true" className="absolute -top-[5px] -left-[5px] w-2.5 h-2.5 border-t-2 border-l-2 border-primary pointer-events-none" />
          <div aria-hidden="true" className="absolute -top-[5px] -right-[5px] w-2.5 h-2.5 border-t-2 border-r-2 border-primary pointer-events-none" />
          <div aria-hidden="true" className="absolute -bottom-[5px] -left-[5px] w-2.5 h-2.5 border-b-2 border-l-2 border-primary pointer-events-none" />
          <div aria-hidden="true" className="absolute -bottom-[5px] -right-[5px] w-2.5 h-2.5 border-b-2 border-r-2 border-primary pointer-events-none" />

          {/* Top Spec Header Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-6 border-b border-border">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center p-1 bg-primary text-primary-foreground">
                <ShieldAlert className="size-4 shrink-0" />
              </span>
              <span className="font-mono text-xs uppercase tracking-widest text-primary font-semibold">
                LOGISTICS SPECIFICATION // ORDER ADVISORY
              </span>
            </div>
            <span className="font-mono text-[11px] text-muted-foreground uppercase tracking-wider">
              DOC REF: SEC-DLV-880
            </span>
          </div>

          {/* Main Content Area */}
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-8 space-y-6">
              <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold uppercase tracking-tight text-foreground leading-tight">
                Plan for Delivery Before the Mechanical Bull Arrives
              </h2>

              <div className="space-y-4 text-muted-foreground text-sm sm:text-base leading-relaxed">
                <p>
                  Large equipment can require more delivery planning than a typical parcel. Before shipment, confirm the packed equipment, destination access and any unloading requirements associated with the exact order.
                </p>
                <p>
                  The bull, inflatable and accessories may also be packed separately depending on the equipment and delivery arrangement. Therefore, review the shipment information for the individual order rather than assuming every mechanical bull arrives the same way.
                </p>
              </div>

              {/* Call to action */}
              <div className="pt-2">
                <Button asChild className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-7 text-xs font-mono uppercase tracking-wider transition-[color,background-color,border-color,box-shadow]">
                  <Link to="/ShippingWarrantyAndOrderingPage" className="inline-flex items-center gap-2">
                    <span>View Shipping Information</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Industrial Spec Checklist Panel */}
            <div className="lg:col-span-4 bg-muted/50 border border-border p-5 space-y-4">
              <div className="border-b border-border pb-2">
                <span className="font-mono text-xs font-bold text-foreground uppercase tracking-wider block">
                  Shipment Checkpoints
                </span>
              </div>

              <div className="space-y-3.5">
                {logisticsPillars.map((item, index) => {
                const Icon = item.icon;
                return <div key={item.label} data-index={index} className="flex items-start gap-3">
                      <div className="p-1.5 bg-background border border-border text-foreground shrink-0 mt-0.5">
                        <Icon className="size-3.5" />
                      </div>
                      <div className="space-y-0.5">
                        <span className="font-mono text-[11px] font-semibold text-foreground tracking-wide block">
                          {item.label}
                        </span>
                        <span className="text-xs text-muted-foreground block leading-tight">
                          {item.detail}
                        </span>
                      </div>
                    </div>;
              })}
              </div>

              <div className="pt-3 border-t border-border">
                <p className="font-mono text-[10px] uppercase text-muted-foreground tracking-tight leading-normal">
                  COMMERCIAL EQUIPMENT NOTICE // ENSURE SUFFICIENT GATE AND DOOR CLEARANCE AT RECEIVING SITE.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}